import { useCallback, useEffect, useRef, useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

import { getQuickQuote } from '@/apiCalls/matchingEngine';
import { Skeleton } from '@/components/Skeleton';
import {
    ELoanUsage,
    EVehicleAssetType,
    selectLoanTerm,
    selectLoanUsage,
    selectPurchasePrice,
    selectSelfAssessedCreditHistory,
    selectVehicleAssetType,
} from '@/context/borrower';
import {
    EDisplays,
    EModals,
    selectInitialLoad,
    selectIsLoading,
    selectIsNZ,
    selectPartnerName,
    setModal,
} from '@/context/display';
import { ILender, selectLenders, setAndSortLenders } from '@/context/lender';
import { store } from '@/context/store';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { useCopy } from '@/copy';
import { TRUCKSTOP_PARTNER_NAME } from '@/partners/truckstop';
import { getCheapestLender } from '@/utils/getCheapestLender';
import { roundToTwoDecimalPlaces } from '@/utils/roundToTwoDecimalPlaces';
import { updateShownLenders } from '@/utils/updateShownLenders';


interface ILenderRowProps {
    title: string;
    value: string;
    valueClass?: string;
    isPending?: boolean;
}

function useIsInitialRender(): boolean {
    const initialRenderRef = useRef(true);
    useEffect(() => {
        initialRenderRef.current = false;
    }, []);

    return initialRenderRef.current;
}

function LenderRow({
    title,
    value,
    valueClass = '',
    isPending = false,
}: ILenderRowProps): JSX.Element {
    const initialLoad = useAppSelector(selectInitialLoad);
    const isLoading = useAppSelector(selectIsLoading);

    const pending = isLoading || isPending;

    return (
        <div className='_flex w-full justify-center'>
            <div className='_flex flex-col justify-start items-start'>
                <h3
                    className='fieldHeading pb-0 text-[0.5rem]'
                    dangerouslySetInnerHTML={{ __html: title }}
                />
                <h2
                    className={`whitespace-normal overflow-hidden h-8 text-black ${valueClass}`}
                >
                    {/* Before the first quote this rendered nothing at all, so
                        the card was a blank space and the screen read as
                        broken rather than loading. */}
                    {!initialLoad || pending ? (
                        <Skeleton className='h-5 w-16 mt-1' />
                    ) : (
                        value
                    )}
                </h2>
            </div>
        </div>
    );
}

export function GenericLenderCard(): JSX.Element {
    const lenders = useAppSelector(selectLenders);
    const [cheapestLender, setCheapestLender] = useState<ILender | undefined>(
        getCheapestLender()
    );
    const [isQuotePending, setIsQuotePending] = useState<boolean>(false);

    const dispatch = useAppDispatch();
    const partnerName = useAppSelector(selectPartnerName);
    const loanUsage = useAppSelector(selectLoanUsage);
    const isNZ = useAppSelector(selectIsNZ);
    const partner = useAppSelector((state) => state.displayReducer.partner);

    const purchasePrice = useAppSelector(selectPurchasePrice);
    const loanTerm = useAppSelector(selectLoanTerm);
    const selfAssessedCredit = useAppSelector(selectSelfAssessedCreditHistory);

    const quickQuoteCallTimeoutRef = useRef<any>(null);
    const hasInitialQuoteBeenCalled = useRef(false);
    const isInitialRender = useIsInitialRender(); // use the custom hook
    const quoteText = store.getState().displayReducer.partner?.quoteText;
    const {
        estimateLabel,
        estimateHeading,
        showEstimateFootnote,
        disclaimerOnHeading,
        darkEstimate,
    } = useCopy().quickCalculator;
    const isAssetTypePersonalLoan =
        useAppSelector(selectVehicleAssetType) ===
        EVehicleAssetType.PERSONAL_LOAN;

    const estimateHeadingText = isAssetTypePersonalLoan
        ? 'Borrow from just*'
        : quoteText
        ? quoteText + '*'
        : estimateHeading;
    const initialLoad = useAppSelector(selectInitialLoad);
    const cardLoading = useAppSelector(selectIsLoading);

    // Handle initial load with URL parameters
    useEffect(() => {
        if (
            initialLoad &&
            purchasePrice &&
            purchasePrice > 0 &&
            !hasInitialQuoteBeenCalled.current
        ) {
            // Clear any pending debounced calls
            if (quickQuoteCallTimeoutRef.current) {
                clearTimeout(quickQuoteCallTimeoutRef.current);
            }

            // Settle briefly first. This runs on the first price above zero,
            // which for a customer typing is the first digit — so it fired a
            // request for $4 on the way to $45,000, the engine rejected it,
            // and the 400 arrived as an unhandled rejection. A price that
            // arrived with the page is already whole, so the wait costs it
            // nothing; a price being typed coalesces into one request.
            // Set when the call is scheduled, not when it fires: the flag
            // means the initial path has been taken, and the effect below
            // uses it to decide whether clearing the price should clear the
            // quote. Setting it late left that stuck until the timer ran.
            hasInitialQuoteBeenCalled.current = true;

            quickQuoteCallTimeoutRef.current = setTimeout(() => {
                getQuickQuote();
            }, 400);
        }

        return () => {
            if (quickQuoteCallTimeoutRef.current) {
                clearTimeout(quickQuoteCallTimeoutRef.current);
            }
        };
    }, [initialLoad, purchasePrice]);

    // This handles user changes to purchasePrice, loanTerm, or loanUsage from frontend
    useEffect(() => {
        // Skip if initial quote hasn't been called yet (wait for initial load to complete)
        if (!hasInitialQuoteBeenCalled.current) {
            return;
        }

        // Skip the immediate trigger after initial render (to avoid double call on mount)
        if (isInitialRender) {
            return;
        }

        // No purchase price (field cleared or state reset on logout) — the
        // displayed quote no longer matches the inputs, so clear it
        if (!purchasePrice || purchasePrice <= 0) {
            setIsQuotePending(false);
            dispatch(setAndSortLenders([]));
            return;
        }

        if (quickQuoteCallTimeoutRef.current) {
            clearTimeout(quickQuoteCallTimeoutRef.current);
        }

        // Show the spinner straight away so the previous quote isn't
        // displayed against inputs it no longer matches
        setIsQuotePending(true);

        // Debounce user changes to avoid excessive API calls
        quickQuoteCallTimeoutRef.current = setTimeout(() => {
            // getQuickQuote turns isLoading on synchronously, so the spinner
            // stays up when the pending flag drops
            getQuickQuote();
            setIsQuotePending(false);
        }, 1500);

        // Cleanup timeout on unmount or when dependencies change
        return () => {
            if (quickQuoteCallTimeoutRef.current) {
                clearTimeout(quickQuoteCallTimeoutRef.current);
            }
        };
    }, [purchasePrice, loanTerm, loanUsage]);

    useEffect(() => {
        setCheapestLender(getCheapestLender());
    }, [lenders]);

    useEffect(() => {
        updateShownLenders(cheapestLender, EDisplays.QUICK_CALCULATOR, false);
    }, [cheapestLender]);

    useEffect(() => {
        if (!isInitialRender) {
            getQuickQuote();
        }
    }, [selfAssessedCredit]);

    const {
        weeklyRepaymentAmount,
        advertisedRateInPercent,
        comparisonRateInPercent,
    } = cheapestLender ?? {};
    // LenderRow used to hold each figure back until the first load had
    // happened; the card keeps that, so nothing flashes a dash on the way in.
    const cardIsPending = cardLoading || isQuotePending || !initialLoad;

    // The same conditions the three-column grid used to encode.
    const showRateLine =
        partnerName !== TRUCKSTOP_PARTNER_NAME &&
        loanUsage !== ELoanUsage.NOVATED &&
        partner?.showRates !== false;
    const showComparisonRate =
        showRateLine && loanUsage !== ELoanUsage.BUSINESS && !isNZ;

    const weeklyRepayment = weeklyRepaymentAmount
        ? `$${weeklyRepaymentAmount.toLocaleString()}`
        : '-';
    const advertisedRate = advertisedRateInPercent
        ? `${roundToTwoDecimalPlaces(advertisedRateInPercent)}%`
        : '-';
    const comparisonRate = comparisonRateInPercent
        ? `${roundToTwoDecimalPlaces(comparisonRateInPercent)}%`
        : '-';

    // ***** Callbacks *****
    const handleClick = useCallback(() => {
        dispatch(setModal(EModals.DISCLAIMER));
    }, [dispatch]);

    return (
        <>
            {estimateLabel && (
                <div className='_flex space-x-1 items-center'>
                    <p className='fieldHeading pb-0'>{estimateLabel}</p>
                    <InformationCircleIcon
                        height={20}
                        width={20}
                        className='text-black cursor-pointer'
                        onClick={handleClick}
                    />
                </div>
            )}
            <div
                className={
                    darkEstimate
                        ? 'flex flex-col font-bold text-white'
                        : `
                    flex flex-col
                    text-center
                    font-bold
                    p-2
                    pt-3
                    shadow-md
                    rounded-[10px]
                    bg-white
                    text-black
                `
                }
            >
                {/* The dark layout carries its own heading, so this one would
                    print the same words a second time - in black, on black. */}
                {!darkEstimate && (
                    <div className='text text-left pl-6 text-black mb-2'>
                        {disclaimerOnHeading ? (
                            <span className='_flex items-center space-x-1'>
                                <span>{estimateHeadingText}</span>
                                <InformationCircleIcon
                                    height={20}
                                    width={20}
                                    className='text-black cursor-pointer shrink-0'
                                    onClick={handleClick}
                                />
                            </span>
                        ) : (
                            estimateHeadingText
                        )}
                    </div>
                )}
                {/* The repayment leads, the rates sit under a rule as one
                    line, and the note closes the card. Three equal columns
                    gave the two rates the same weight as the number people
                    are actually here for. */}
                {darkEstimate ? (
                    // Repayment on the left, rates stacked on the right: the
                    // number people came for leads, and the rates qualify it
                    // without competing for the same line.
                    <div className='_flex items-end justify-between gap-3 text-left'>
                        <div>
                            <p className='_flex items-center gap-1 text-sm font-normal text-panelMuted'>
                                {estimateHeadingText}
                                {/* The only way into the disclaimer modal, so
                                    it has to survive the dark layout too. */}
                                {disclaimerOnHeading && (
                                    <InformationCircleIcon
                                        height={16}
                                        width={16}
                                        className='cursor-pointer shrink-0'
                                        onClick={handleClick}
                                    />
                                )}
                            </p>
                            <div className='_flex items-baseline gap-1'>
                                <span className='text-4xl font-bold text-white'>
                                    {cardIsPending ? (
                                        <Skeleton className='h-9 w-28' />
                                    ) : (
                                        weeklyRepayment
                                    )}
                                </span>
                                <span className='text-sm font-normal text-panelMuted'>
                                    /week
                                </span>
                            </div>
                        </div>
                        {showRateLine && (
                            <div className='text-right shrink-0'>
                                <p className='text-sm font-normal text-panelMuted'>
                                    <span className='font-bold text-white'>
                                        {advertisedRate}
                                    </span>{' '}
                                    advertised
                                </p>
                                {showComparisonRate && (
                                    <p className='text-sm font-normal text-panelMuted'>
                                        <span className='font-bold text-white'>
                                            {comparisonRate}
                                        </span>{' '}
                                        comparison
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                    /* Unchanged for every partner but CarExpert: the original
                       three-column grid of rows. The stacked layout above is
                       the CarExpert design and is reached through
                       darkEstimate. */
                    <div
                        className={`grid ${
                            partnerName === TRUCKSTOP_PARTNER_NAME ||
                            loanUsage === ELoanUsage.NOVATED
                                ? 'grid-cols-1'
                                : loanUsage !== ELoanUsage.BUSINESS && !isNZ
                                ? 'grid-cols-3'
                                : 'grid-cols-2'
                        }`}
                    >
                        <LenderRow
                            title='Per week'
                            value={weeklyRepayment}
                            valueClass='text-2xl'
                            isPending={isQuotePending}
                        />
                        {partnerName !== TRUCKSTOP_PARTNER_NAME &&
                            loanUsage !== ELoanUsage.NOVATED &&
                            partner?.showRates !== false && (
                                <>
                                    <LenderRow
                                        title='Advertised Rate'
                                        value={advertisedRate}
                                        valueClass='text-sm'
                                        isPending={isQuotePending}
                                    />
                                    {loanUsage !== ELoanUsage.BUSINESS &&
                                        !isNZ && (
                                            <LenderRow
                                                title='Comparison Rate'
                                                value={comparisonRate}
                                                valueClass='text-sm'
                                                isPending={isQuotePending}
                                            />
                                        )}
                                </>
                            )}
                    </div>
                )}
            </div>
        </>
    );
}
